import React, {useCallback, useEffect, useState} from 'react';
import {
    Box,
    Button,
    DialogActions,
    DialogContentText,
    FormControl,
    InputAdornment,
    MenuItem,
    Select,
    TextField,
    Tooltip,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import {formatDate, checkDateInput} from '../../../utils/date.js';
import PropTypes from 'prop-types';
import {ezprOrange} from '../../_helpers.js';
import UserSettings from '../../../models/cloudobjects/UserSettings.js';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Link from '@mui/material/Link';
import {blue} from '@mui/material/colors';
import {BrickContext} from 'olympe';

/**
 * This is the content of the User Defaults popup, settings tab
 * @param {UserSettingsContent.propTypes} props
 * @return {JSX.Element}
 * @constructor
 */
export function UserSettingsContent(props) {
    // if the use has no entry in the DB then the response for userSettings will be a JSON
    const retrievedUserSettings = props.userSettings.dateFormat ? props.userSettings : props.userSettings.getUserSettingsObject();
    const [dateFormat, setDateFormat] = useState(retrievedUserSettings?.dateFormat || 'dd/MM/yyyy');
    const [currencySeparator, setCurrencySeparator] = useState(retrievedUserSettings?.separator || 'space');

    const currentUserData = props.currentUser && typeof props.currentUser.toJSON === 'function' ? props.currentUser.toJSON() : props.currentUser;
    const normalizedCurrentUserEmail = currentUserData && currentUserData['SAML name id'] ? currentUserData['SAML name id'].toLowerCase() : '';

    const adminUserList = JSON.parse(props.adminAppUserList);
    const showAdminLink = adminUserList.some((user) => user.email.toLowerCase() === normalizedCurrentUserEmail) ||
        (props.superUserList && props.superUserList.some((user) => {
            const userData = user && typeof user.toJSON === 'function' ? user.toJSON() : user;
            return (userData.EmailId || '').toLowerCase() === normalizedCurrentUserEmail;
        }));

    const handleDateFormatChange = (value) => {
        const newValue = checkDateInput(value.target.value);
        setDateFormat(newValue);
    };

    const handleCurrencyOperatorChange = (value) => {
        setCurrencySeparator(value.target.value);
    };

    const handleSave = () => {
        props.handleSave({
            separator: currencySeparator,
            dateFormat: dateFormat,
            tag: (props.userSettings instanceof UserSettings) ? props.userSettings.getTag() : null,
        });
    };

    const formatedDate = formatDate(dateFormat);
    const saveBtnDisabled = formatedDate === 'Invalid Date Format' || formatedDate ==='No date format provided';
    return (
        <>
            <div className={'popup-user-defaults-content '}>
                <div style={{display: 'flex', alignItems: 'center', gap: '5px'}}>
                    <p>Date Format:</p>
                    <Tooltip
                        title={<div>
                            <p>yyyy is the year, with four digits (0000 to 9999).</p>
                            <p>MM is the month, with two digits (01 to 12).</p>
                            <p>dd is the day of the month, with two digits (01 to 31).</p>
                        </div>}
                        style={{whiteSpace: 'pre-line'}}>
                        <InfoOutlinedIcon fontSize="inherit"/>
                    </Tooltip>
                </div>
                <div>
                    <Box sx={{flexDirection: 'column'}}>
                        <TextField
                            label=""
                            value={dateFormat}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <EventIcon />
                                    </InputAdornment>
                                ),
                            }}
                            fullWidth={true}
                            variant="standard"
                            onChange={handleDateFormatChange}
                        />
                        <DialogContentText>{formatedDate}</DialogContentText>
                    </Box>
                </div>
                <p>Currency separator:</p>
                <div>
                    <FormControl fullWidth>
                        <Select
                            size={'small'}
                            value={currencySeparator}
                            onChange={handleCurrencyOperatorChange}
                        >
                            <MenuItem value={'comma'}>Separator (123,456.78)</MenuItem>
                            <MenuItem value={'point'}>Separator (123.456,78)</MenuItem>
                            <MenuItem value={'space'}>Separator (123 456,78)</MenuItem>
                        </Select>
                    </FormControl>
                </div>
            </div>
            <DialogActions sx={showAdminLink ?
                {
                    justifyContent: 'space-between',
                    width: '100%',
                } :
                {
                    justifyContent: 'flex-end',
                    width: '100%',
                }}
            >
                {showAdminLink && (
                    <Box sx={{marginLeft: '-0.5rem'}}>
                    Click <Link
                            href="/admin-app/"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                'color': blue[500],
                                'textDecorationColor': blue[500],
                                '&:hover': {
                                    color: blue[700],
                                    textDecorationColor: blue[700],
                                },
                            }}>
                    here
                        </Link> to access the admin app
                    </Box>
                )}
                <Button
                    className="popup-user-settings-button"
                    sx={{
                        backgroundColor: ezprOrange,
                        color: 'white',
                        marginRight: '-0.5rem',
                    }}
                    variant={'contained'}
                    onClick={handleSave}
                    disabled={saveBtnDisabled}
                >
        Save
                </Button>
            </DialogActions>
        </>
    );
}

UserSettingsContent.propTypes = {
    handleSave: PropTypes.func,
    userSettings: PropTypes.oneOfType([
        PropTypes.instanceOf(UserSettings),
        PropTypes.object,
    ]),
    adminAppUserList: PropTypes.string,
    superUserList: PropTypes.array,
    currentUser: PropTypes.object,
    context: BrickContext,
};
